import { typography, radius } from '@/shared/theme/tokens';
import { REC_IMPACT_META, OPEN_LOOP_META, HEADLINE_META, withAlpha } from '../../liveModeMeta';
import { BCAST } from './broadcastMeta';

/**
 * The lower-third — broadcast's anchor graphic for the single top recommendation
 * (`rankedRecs[0]`). Headline treatment: a coloured kicker strap, the parameter
 * as the anchor name, the setpoint delta as the lede, and the expected impact as
 * the supporting line. Read-only: the whole card is the click target → onSelect.
 *
 * Honest states:
 *  - degraded → "ADVISORY PAUSED — STALE DATA" (recs are already suppressed)
 *  - no recs  → calm "NO ACTIONS ON AIR"
 */
export function LowerThird({ rec, degraded, animate, onSelect }) {
  if (degraded) {
    const m = HEADLINE_META.degraded;
    return (
      <PausedCard
        color={m.color}
        icon="sensors_off"
        kicker="SIGNAL ADVISORY"
        title="ADVISORY PAUSED — STALE DATA"
        sub="Recommendations are held off air until live inputs recover."
      />
    );
  }
  if (!rec) {
    return (
      <PausedCard
        color={BCAST.ok}
        icon="check_circle"
        kicker="ALL CLEAR"
        title="NO ACTIONS ON AIR"
        sub="Nothing needs an operator decision right now."
      />
    );
  }

  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const openLoop = rec.status === 'accepted';
  const clickable = typeof onSelect === 'function';

  return (
    <div
      onClick={clickable ? () => onSelect(rec) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
      style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        background: `linear-gradient(180deg, ${withAlpha(impact.color, '12')} 0%, ${BCAST.panel} 60%)`,
        borderLeft: `4px solid ${impact.color}`, borderRadius: radius.sm, overflow: 'hidden',
        border: `1px solid ${BCAST.lineBright}`, borderLeftWidth: 4,
        cursor: clickable ? 'pointer' : 'default',
        animation: animate ? 'bc-fade 0.35s ease' : 'none',
      }}
    >
      {/* Kicker strap */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
        background: BCAST.chrome, borderBottom: `1px solid ${BCAST.line}`,
      }}>
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.extrabold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase',
          color: BCAST.studio, background: BCAST.brand, padding: '3px 9px', borderRadius: radius.xs,
        }}>
          TOP RECOMMENDATION
        </span>
        <Tag color={impact.color}>{impact.label} IMPACT</Tag>
        {openLoop && <Tag color={OPEN_LOOP_META.color}>{OPEN_LOOP_META.label}</Tag>}
        <span style={{
          marginLeft: 'auto', fontFamily: BCAST.mono, fontSize: typography.fontSize.xs,
          color: BCAST.inkFaint, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}>
          Δ IN {rec.timeToImpact}
        </span>
      </div>

      {/* Anchor body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, padding: '16px 20px' }}>
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.extrabold,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: BCAST.ink, lineHeight: 1.05,
        }}>
          {rec.parameterName}
        </span>

        {numerical ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', fontFamily: BCAST.mono, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>NOW</span>
              <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize['4xl'], color: BCAST.inkDim }}>{rec.action.currentValue}</span>
            </span>
            <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize['4xl'], color: impact.color }}>──▶</span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: impact.color, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>SET TO</span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.extrabold, color: impact.color, lineHeight: 1 }}>
                  {rec.action.recommendedValue}
                </span>
                {rec.unit && <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.lg, color: BCAST.inkFaint }}>{rec.unit}</span>}
              </span>
            </span>
          </div>
        ) : (
          <span style={{
            fontFamily: typography.fontFamily.body, fontSize: typography.fontSize['2xl'], color: BCAST.ink, lineHeight: 1.4,
          }}>
            {rec.action?.instruction}
          </span>
        )}

        {rec.expectedImpact && (
          <span style={{
            fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.md, color: BCAST.inkDim, lineHeight: 1.5,
            borderTop: `1px solid ${BCAST.line}`, paddingTop: 10,
          }}>
            {rec.expectedImpact}
          </span>
        )}
      </div>
    </div>
  );
}

function PausedCard({ color, icon, kicker, title, sub }) {
  return (
    <div style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, textAlign: 'center', padding: 28,
      background: `linear-gradient(180deg, ${withAlpha(color, '10')} 0%, ${BCAST.panel} 70%)`,
      border: `1px solid ${BCAST.lineBright}`, borderLeft: `4px solid ${color}`, borderRadius: radius.sm,
    }}>
      <span style={{
        fontFamily: BCAST.heading, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.extrabold,
        letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color,
      }}>
        {kicker}
      </span>
      <span className="material-symbols-outlined" style={{ fontSize: 34, color }}>{icon}</span>
      <span style={{
        fontFamily: BCAST.heading, fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.extrabold,
        letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: BCAST.ink,
      }}>
        {title}
      </span>
      <span style={{ fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.md, color: BCAST.inkDim, maxWidth: 340, lineHeight: 1.5 }}>
        {sub}
      </span>
    </div>
  );
}

function Tag({ color, children }) {
  return (
    <span style={{
      fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
      letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      color, background: withAlpha(color, '1f'), border: `1px solid ${withAlpha(color, '66')}`,
      borderRadius: radius.xs, padding: '2px 7px',
    }}>
      {children}
    </span>
  );
}
